import { Button, Card, Col, Form, Row } from "antd";
import FullPageLayout from "../../../../templates/FullPageLayout";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { InputField, InputPhone } from "../../../../atoms/FormFields";
import { useLocation, useNavigate } from "react-router-dom";
import { AddressComponent, Place } from "../../../Company/ducks/types";
import { AutoCompleteField } from "../../../../atoms/FormFields/AutoComplete";
import ErrorMessage from "../../../../atoms/Toasts/ErrorMessage";
import { AxiosError } from "axios";
import SuccessMessage from "../../../../atoms/Toasts/SuccessMessage";
import { updateCrewYardById } from "../ducks/services";
import { useAppDispatch, useAppSelector } from "../../../../config/hooks";
import {
  getCrewYardDetailById,
  removeCrewYardDetailData,
} from "../ducks/actions";
import { NeedHelp } from "../../../../atoms/CustomIcons";

interface FormValues {
  name: string;
  address: string;
  cont_person_first_name: string;
  cont_person_last_name: string;
  cont_person_email: string;
  cont_person_phone: string;
}

export default function EditCrewYards() {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();
  const url = useLocation();
  const dispatch = useAppDispatch();
  const id = url?.pathname.split("/")[4];
  const companyId = url?.pathname.split("/")[3];
  const [loading, setLoading] = useState(false);
  const [googleAddress, setGoogleAddress] = useState<Place | null>(null);
  const navigate = useNavigate();
  const crewYardDetailData = useAppSelector(
    (state) => state?.crewYard?.crewYardDetailData
  );

  const onSubmit = (val: FormValues) => {
    let city = "";
    let state = "";
    googleAddress?.address_components?.forEach(
      (component: AddressComponent) => {
        if (component.types.includes("locality")) {
          city = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          state = component.short_name;
        }
      }
    );

    setLoading(true);
    const payload = {
      name: val.name,
      address: val?.address,
      city: city !== "" ? city : crewYardDetailData?.data?.city,
      state: state !== "" ? state : crewYardDetailData?.data?.state,
      cont_person_first_name: val?.cont_person_first_name
        ? val?.cont_person_first_name
        : undefined,
      cont_person_last_name: val?.cont_person_last_name
        ? val?.cont_person_last_name
        : undefined,
      cont_person_email: val?.cont_person_email
        ? val?.cont_person_email
        : undefined,
      cont_person_phone: val?.cont_person_phone
        ? val?.cont_person_phone
        : undefined,
      company_id: companyId,
    };

    updateCrewYardById(id, payload)
      .then(() => {
        setLoading(false);
        SuccessMessage("Changes saved");
        navigate(`/company-settings/crew-yards/${companyId}`);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (id) {
      dispatch(getCrewYardDetailById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    dispatch(removeCrewYardDetailData());
  }, [dispatch]);

  useEffect(() => {
    if (crewYardDetailData?.data) {
      setValue("address", crewYardDetailData?.data?.address);
      setValue("name", crewYardDetailData?.data?.name);
      setValue(
        "cont_person_first_name",
        crewYardDetailData?.data?.cont_person_first_name ?? ""
      );
      setValue(
        "cont_person_last_name",
        crewYardDetailData?.data?.cont_person_last_name ?? ""
      );
      setValue(
        "cont_person_email",
        crewYardDetailData?.data?.cont_person_email ?? ""
      );
      setValue(
        "cont_person_phone",
        crewYardDetailData?.data?.cont_person_phone ?? ""
      );
    }
  }, [crewYardDetailData, setValue, dispatch]);

  return (
    <FullPageLayout loading={crewYardDetailData?.loader || loading}>
      <Button className="btn-sm fixedTopBtn">
        <NeedHelp /> Need help?
      </Button>
      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <Col style={{ width: "672px", paddingBottom: 72 }}>
          <div
            className="title"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2> Edit crew yard </h2>
          </div>
          <Card>
            <Row gutter={20}>
              <Col span={24}>
                <h4 style={{ marginBottom: "16px" }}> Crew yard details </h4>
              </Col>
              <Col span={24}>
                <InputField
                  fieldName="name"
                  label="Yard name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter yard name",
                  }}
                  isRequired
                  rules={{
                    required: "Yard name is required.",
                  }}
                  validate={errors.name && "error"}
                  validMessage={errors.name && errors.name.message}
                />
              </Col>
              <Col span={24} className="mb-0">
                <AutoCompleteField
                  fieldName="address"
                  label="Address"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter address",
                  }}
                  onPlaceChange={(place: Place) => setGoogleAddress(place)}
                  isRequired
                  rules={{
                    required: "Address is required.",
                  }}
                  validate={errors.address && "error"}
                  validMessage={errors.address && errors.address.message}
                />
                {/* <SelectField
                  fieldName="crews_assigned"
                  label="Crews assigned"
                  control={control}
                  selectOption={crews_assigned_options}
                  iProps={{
                    placeholder: "Select Crews assigned",
                    mode: "tags",
                  }}
                  isRequired
                  rules={{
                    required: "Crews assigned is required.",
                  }}
                  validate={errors.crews_assigned && "error"}
                  validMessage={
                    errors.crews_assigned && errors.crews_assigned.message
                  }
                  tooltipTitle="The crew yard will be suggested as a default starting and ending location for these crews."
                /> */}
              </Col>
            </Row>
          </Card>

          <Card>
            <Row gutter={20}>
              <Col span={24}>
                <h4 style={{ marginBottom: "16px" }}> Contact person </h4>
              </Col>
              <Col span={12}>
                <InputField
                  fieldName="cont_person_first_name"
                  label="First name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter first name",
                  }}
                />
              </Col>
              <Col span={12}>
                <InputField
                  fieldName="cont_person_last_name"
                  label="Last name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter last name",
                  }}
                />
              </Col>
              <Col span={12} className="mb-0">
                <InputField
                  fieldName="cont_person_email"
                  label="Email"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter email",
                  }}
                  rules={{
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: "Incorrect email address.",
                    },
                  }}
                  validMessage={
                    errors.cont_person_email && errors.cont_person_email.message
                  }
                />
              </Col>
              <Col span={12} className="mb-0">
                <InputPhone
                  fieldName="cont_person_phone"
                  label="Phone"
                  control={control}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <div className="fixed_btm_card">
          <Card>
            <div className="centered">
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={() =>
                  navigate(`/company-settings/crew-yards/${companyId}`)
                }
              >
                Cancel
              </Button>
              <div style={{ display: "flex", gap: "16px" }}>
                <Button
                  htmlType="submit"
                  type="primary"
                  className="mint-btn"
                  // disabled={true}
                >
                  Save changes
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </Form>
    </FullPageLayout>
  );
}
